import { RTRValidationUtils, WholeLifeValidationUtils } from '@hit/validation-utils';
import { WHOLE_LIFE_PRODUCT_CODE } from '../../../../common/js/constants/policy';
import { ProductType } from '../../../../common/js/interfaces/CommonTypes';
import { CommonUtils } from '../../../../common/js/static/CommonUtils';
import { LowFaceWholeLife, Product, productSpecs } from '../../../../common/js/static/Product';
import { RateClasses } from '../../../../common/js/static/RateClasses';
import type { ValidationResult } from '../../interfaces/validation/ValidationInterface';
import { CP_EXCEPTION_MINIMUM_FACE_AMOUNT, CP_EXCEPTION_PRODUCTS, CP_MINIMUM_FACE_AMOUNT } from './product-rules-table';
import { Utils } from '../../../../common/js/Utils';

// Validators for logic shared between Angular and Non-Angular (Semotus/Backend) code
// Angular libraries cannot be used in this file.
export class ValidationStatic {
  private static getWindow(): any {
    return window;
  }

  public static isValidRtrAge(
    productCode: string,
    faceAmount: number,
    rtrAmount: number,
    dateOfBirth: Date
  ): ValidationResult {
    const rtrAgeValidation = RTRValidationUtils.isValidRtrAge(productCode, faceAmount, rtrAmount, dateOfBirth);
    if (rtrAgeValidation && rtrAgeValidation.isValid) {
      return { result: true };
    } else {
      return {
        result: false,
        error: rtrAgeValidation && rtrAgeValidation.message ? rtrAgeValidation.message : 'Could not validate RTR age',
      };
    }
  }

  public static isMaximumFaceAmountMetForWhole({
    dob,
    product,
    faceAmount,
    maxFaceAmount,
    isPolicyWLandLowFace = 'NA',
    isUsCitizen,
  }: {
    dob: Date;
    product: WHOLE_LIFE_PRODUCT_CODE;
    faceAmount: number;
    maxFaceAmount?: number;
    isPolicyWLandLowFace?: 'NA' | 'true' | 'false';
    isUsCitizen: boolean;
  }): true | number {
    let maxAllowed;
    if (isPolicyWLandLowFace === 'true') {
      let maxFaceWL = null;
      if (typeof window !== 'undefined') {
        const customer = (window as any).controller.customer;
        maxFaceWL = customer.application?.primaryPolicy?.maxFaceWholeLife;
      }
      maxAllowed = maxFaceWL;
    } else if (maxFaceAmount) {
      maxAllowed = maxFaceAmount;
    } else {
      maxAllowed = ValidationStatic.getMaximumAllowedForWhole({
        dob,
        product,
        isUsCitizen,
      });
    }
    if (faceAmount > maxAllowed) {
      return maxAllowed;
    }
    return true;
  }

  public static getMinimumFaceAmountAllowed({
    isJoint,
    productType,
    product = '',
    healthClass,
    lowFaceWholeLifeEnabled = false,
    isPolicyWLandLowFace = 'NA',
    dob,
  }: {
    isJoint?: boolean;
    productType: ProductType;
    product?: string;
    healthClass: string;
    lowFaceWholeLifeEnabled?: boolean;
    isPolicyWLandLowFace?: 'NA' | 'true' | 'false';
    dob?: Date;
  }): number {
    // Keep exception behavior until low face whole life is completely released
    // Can use same validation as solo whole life afterward
    const isException = isJoint && CP_EXCEPTION_PRODUCTS.includes(productType) && !lowFaceWholeLifeEnabled;
    let lowFaceWL10Enabled = false;
    if (typeof window !== 'undefined') {
      const controller = (window as any).controller;
      const policy = (window as any).controller.customer.application?.primaryPolicy;
      const lowFaceWL10OverrideEnabled: boolean = controller.lowFaceWL10OverrideEnabled ?? false;
      lowFaceWL10Enabled = ValidationStatic.isLowFaceWL10Enabled(
        controller.lowFaceWL10EnableDate,
        policy?.submittedAt,
        lowFaceWL10OverrideEnabled
      );
    }

    let min;
    //Amendments validation can have table rated healthClass
    if (RateClasses.isTableRated(RateClasses.findByStringCode(healthClass).code)) {
      if (productType === ProductType.TERM) {
        min = 1e5;
      } else if (isException) {
        min = 5e4;
      } else if (productType === ProductType.WHOLE) {
        if (isPolicyWLandLowFace === 'true') {
          min = LowFaceWholeLife.minFace;
        } else {
          min = 5e4;
        }
      }
    } else {
      // The minimum face amounts for Term quotes are the same across products (regular and ecp)
      // The minimum face amounts for WL products are different across products (CPL100, 65, 20, 15, 12 and 10)
      // if isException is true, we know for sure it's WL and go to the right table.
      // if isException is false, then we need to look up one path in CP_MINIMUM_FACE_AMOUNT for WL
      // and a different path for Term
      if (isException) {
        min = CP_EXCEPTION_MINIMUM_FACE_AMOUNT[productType][product][healthClass];
      } else if (productType === ProductType.WHOLE) {
        if (isPolicyWLandLowFace === 'NA' && !(product === Product.products.CPL10A.code && lowFaceWL10Enabled)) {
          min = CP_MINIMUM_FACE_AMOUNT[productType][product][healthClass];
        } else if (product === Product.products.CPL10A.code && lowFaceWL10Enabled) {
          min = this.getMinFaceAmountFromValidationUtils(
            dob,
            healthClass,
            product,
            lowFaceWholeLifeEnabled,
            lowFaceWL10Enabled
          );
          min = isPolicyWLandLowFace !== 'false' ? min : 5e4;
        } else {
          min = isPolicyWLandLowFace === 'true' ? LowFaceWholeLife.minFace : 5e4;
        }
      } else {
        min = CP_MINIMUM_FACE_AMOUNT[productType][healthClass];
      }
    }

    return min;
  }

  public static isLowFaceWL10Enabled(lowFaceWL10EnableDateFF, submittedAt, lowFaceWL10OverrideEnabled): boolean {
    const lowFaceWL10EnableDate: Date = lowFaceWL10EnableDateFF ? new Date(lowFaceWL10EnableDateFF) : null;
    const lowFaceWL10Enabled: boolean =
      !!lowFaceWL10OverrideEnabled ||
      (!!lowFaceWL10EnableDate &&
        ((!!submittedAt && submittedAt >= lowFaceWL10EnableDate) ||
          (!submittedAt && new Date() >= lowFaceWL10EnableDate)));

    return lowFaceWL10Enabled;
  }

  public static getMinFaceAmountFromValidationUtils(
    dob,
    healthClass,
    product,
    lowFaceWholeLifeEnabled,
    lowFaceWL10Enabled
  ): number {
    const actualAge = Utils.getActualAgeInFractionalYears(dob);
    const actualAgeLookUpKey = Utils.roundDownToNearestIncrement(actualAge, 0.5).toFixed(1);

    const config = WholeLifeValidationUtils.getConfigForProduct(product, lowFaceWholeLifeEnabled, lowFaceWL10Enabled);
    let min = config.ActualAgeToMinTotalFace[actualAgeLookUpKey];

    if (typeof min === 'object') {
      min = min[healthClass];
    }

    return min;
  }

  static getMaximumAllowedForTerm({ dob, isUsCitizen }: { dob: Date; isUsCitizen: boolean }) {
    if (CommonUtils.isOlderThanOrEqualToAge(dob, Product.productSpecs.CASTerm.maxAgeForHigherFace)) {
      return Product.productSpecs.CASTerm.maxFaceForHigherAge;
    } else {
      return isUsCitizen ? Product.productSpecs.CASTerm.maxFace : Product.productSpecs.CASTerm.maxFaceForNoncitizens;
    }
  }

  static getMaximumAllowedForWhole({
    dob,
    product,
    isUsCitizen,
  }: {
    dob: Date;
    product: WHOLE_LIFE_PRODUCT_CODE;
    isUsCitizen: boolean;
  }) {
    return product === 'CPL65'
      ? ValidationStatic.getMaximumAllowedForCPL65(isUsCitizen)
      : ValidationStatic.getMaximumAllowedForCPLOthers(dob, isUsCitizen);
  }

  private static getMaximumAllowedForCPL65(isUsCitizen: boolean) {
    return isUsCitizen ? Product.productSpecs.CPL65.maxFace : Product.productSpecs.CPL65.maxFaceForNoncitizens;
  }

  // Not ideal. Should be per product.
  private static getMaximumAllowedForCPLOthers(dob: Date, isUsCitizen: boolean) {
    if (dob) {
      if (CommonUtils.isOlderThanOrEqualToAge(dob, Product.productSpecs.CPL100.maxAgeForHigherFace)) {
        return Product.productSpecs.CPL100.maxFaceForHigherAge;
      }
    }
    return isUsCitizen ? Product.productSpecs.CPL100.maxFace : Product.productSpecs.CPL100.maxFaceForNoncitizens;
  }
}
